<template>
  <header class="rpd-header main-header" :class="{
    'front fw-header': $route.path == '/',
    'border-bottom': !['/presale-database', '/', '/custom-login'].includes(
      $route.path
    ),
    'bg-vb-primary': ['/presale-database', '/custom-login'].includes(
      $route.path
    ),
  }">
    <div class="rpd-header__inner">
      <!-- Mobile menu trigger (kept for parent integration) -->
      <div class="hidden-lg hidden-md d-none">
        <div class="fa fa-bars mobile-nav text-vb-primary" v-click-outside="$emit('menu-off')"
          @click="$emit('toggle-menu', !menuOn)"></div>
      </div>

      <!-- Logo -->
      <router-link v-if="
        $route.path != '/' &&
        !['/presale-database', '/', '/custom-login'].includes($route.path)
      " class="rpd-header__logo" :to="marketOverviewRoute" aria-label="Real Property Data home">
        <img class="rpd-header__logo-img" :src="isDark ? logoDark : logoLight" alt="Real Property Data" />
      </router-link>
      <a v-else class="rpd-header__logo" href="/" aria-label="Real Property Data home">
        <img class="rpd-header__logo-img" :src="logoDark" alt="Real Property Data" />
      </a>

      <!-- Optional workspace eyebrow -->
      <template v-if="workspace">
        <span class="rpd-header__divider"></span>
        <div class="rpd-header__workspace">{{ workspace }}</div>
      </template>

      <!-- Spacer -->
      <div class="rpd-header__spacer"></div>

      <!-- Right cluster -->
      <div class="rpd-header__actions">
        <!-- Theme toggle -->
        <button class="rpd-iconbtn" type="button" @click="toggleDark"
          :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'">
          <font-awesome-icon :icon="['fas', isDark ? 'sun' : 'moon']" />
        </button>

        <!-- Admin notifications -->
        <template v-if="isAdmin">
          <b-dropdown variant="link" toggle-class="rpd-iconbtn-toggle" no-caret right
            menu-class="rpd-header__notif-menu" class="rpd-header__bdropdown">

            <template v-if="isSuperAdmin">
              <b-dropdown-header v-if="pendingApprovals.length">DBA Approvals</b-dropdown-header>
              <b-dropdown-item :href="approval.link" target="_blank" v-for="(approval, index) in pendingApprovals"
                :key="'app-' + index">
                <div class="text-muted small font-weight-bold">
                  {{ formatDate(approval.dba.created) }}
                </div>
                <div>{{ approval.title }}</div>
              </b-dropdown-item>

              <b-dropdown-divider v-if="pendingApprovals.length && pendingCustomMls.length" />

              <b-dropdown-header v-if="pendingCustomMls.length">Custom MLS Submissions</b-dropdown-header>
              <b-dropdown-item href="/approval-management" v-for="(custom, index) in pendingCustomMls"
                :key="'custom-' + index">
                <div class="text-muted small font-weight-bold">
                  Pending Custom MLS
                </div>
                <div>{{ custom.name || "New Submission" }}</div>
              </b-dropdown-item>

              <b-dropdown-divider />

              <b-dropdown-item href="/approval-management">
                <div class="font-weight-bold">
                  Approval Management
                  <span class="float-right badge badge-secondary">{{
                    totalPendingCount
                    }}</span>
                </div>
              </b-dropdown-item>
            </template>
          </b-dropdown>
        </template>

        <!-- New project. Admin only, mirroring the server rule — the endpoint
             answers Unauthorized to anyone who is not a Drupal administrator or
             editor, so this only hides a button that would not have worked. -->
        <b-button v-if="isAdmin" class="rpd-newproject" type="button" title="Create a new presale project"
          @click="openNewProject">
          <font-awesome-icon :icon="['fas', 'plus']" />
          <span>New project</span>
        </b-button>

        <!-- Feedback pill -->
        <b-button v-if="userLoggedIn" v-b-modal.feedback-modal class="rpd-feedback" type="button">
          <font-awesome-icon :icon="['fas', 'comment-dots']" />
          <span>Send feedback</span>
        </b-button>

        <!-- Login link (anonymous) -->
        <template v-if="!userStore.current_user.role && !isMobile">
          <span class="rpd-header__divider"></span>
          <a class="rpd-header__login-link" href="/presale-database">LOGIN</a>
        </template>

        <!-- User chip with logout (authenticated, desktop) -->
        <template v-else-if="userStore.current_user.role && !isMobile">
          <span class="rpd-header__divider"></span>
          <b-dropdown variant="link" no-caret right toggle-class="rpd-userchip-toggle"
            menu-class="rpd-header__user-menu" class="rpd-header__bdropdown">
            <template #button-content>
              <span class="rpd-userchip">
                <span class="rpd-userchip__avatar" :style="{ background: avatarColor }">{{ userInitials }}</span>
                <span class="rpd-userchip__meta">
                  <span class="rpd-userchip__name">{{ userName }}</span>
                  <span class="rpd-userchip__role">{{ userRoleLabel }}</span>
                </span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="rpd-userchip__chev" />
              </span>
            </template>
            <b-dropdown-item v-if="canSeeWorklist" to="/tasks">
              <font-awesome-icon :icon="['fas', 'tasks']" class="rpd-menu-icon" />
              <span>Worklist</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="isAdmin" to="/submissions">
              <font-awesome-icon :icon="['fas', 'file-alt']" class="rpd-menu-icon" />
              <span>My MLS Submissions</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="isAdmin" to="/user-management">
              <font-awesome-icon :icon="['fas', 'users-cog']" class="rpd-menu-icon" />
              <span>User Management</span>
            </b-dropdown-item>
            <b-dropdown-divider v-if="isAdmin" />
            <b-dropdown-item @click="$emit('logout')">
              <font-awesome-icon :icon="['fas', 'sign-out-alt']" class="rpd-menu-icon" />
              <span>Logout</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </div>
    </div>

    <b-modal id="feedback-modal" hide-header hide-footer no-close-on-backdrop class="_modal-wrapper feed_back-modal p-0"
      size="md" scrollable>
      <UserFeedback v-on:hideFeedbackModal="hideFeedbackModal" :form-data="formData" />
    </b-modal>

    <!-- Remounted on every open (:key), so a project created in one session is
         not still on screen when the form is opened for the next one. -->
    <b-modal id="new-project-modal" hide-header hide-footer no-close-on-backdrop size="lg"
      modal-class="rpd-newproject-modal">
      <NewPresaleForm :key="newProjectKey" @close="closeNewProject" @open-project="goToProject" />
    </b-modal>
  </header>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, getCurrentInstance } from "vue";
import Vue from "vue";
import ClickOutside from "vue-click-outside";
import { useUserStore } from "../../store/UserStore";
import { BACKEND_URL, isSuperAdmin as isSuperAdminUser } from "../../variables";
import UserFeedback from "./UserFeedback.vue";
import NewPresaleForm from "../Forms/NewPresaleForm.vue";
import { MARKET_OVERVIEW_DEFAULT_ROUTE } from "../../data/marketOverviewQuery";
import logoLight from "../../assets/images/Logo-removebg-preview.png";
import logoDark from "../../assets/images/logo/RPD_logotype_skyblue.png";
import axios from "axios";

// The logo opens Market Overview's default report. The query has to be on the
// link: MarketOverview.vue redirects a query-less route to /list-search, and on
// a route it already occupies its created() hook does not re-run to catch that.
const marketOverviewRoute = MARKET_OVERVIEW_DEFAULT_ROUTE;

const instance = ref(getCurrentInstance());
Vue.directive("click-outside", ClickOutside);
const props = defineProps({
  userLoggedIn: Boolean,
  role: String,
  menuOn: Boolean,
  workspace: { type: String, default: "" },
});
const hideFeedbackModal = () => {
  instance.value.proxy.$bvModal.hide("feedback-modal");
};

// The new-project form, opened from the header so a project can be filed from
// wherever the reader already is rather than only from a list page.
const newProjectKey = ref(0);

const openNewProject = () => {
  newProjectKey.value += 1;
  instance.value.proxy.$bvModal.show("new-project-modal");
};

const closeNewProject = () => {
  instance.value.proxy.$bvModal.hide("new-project-modal");
};

const goToProject = (nid) => {
  closeNewProject();
  const proxy = instance.value.proxy;
  // The project page reads its id once, in data(), and watches only `fid` — so
  // arriving from another project has to remount it. A router push between two
  // /project URLs would leave the previous project on screen.
  if (proxy.$route.path === "/project") {
    window.location.assign(`/project?project=${nid}`);
    return;
  }
  proxy.$router
    .push({ path: "/project", query: { project: String(nid) } })
    .catch(() => { });
};

const formatDate = (date) => {
  if (!date || date.length <= 0) return "-";
  let mlsDate = new Date(date * 1000);
  return mlsDate.toISOString().slice(0, 10).replace(/-/g, "-");
};

const userStore = useUserStore();
const isMobile = computed(() => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) ||
    window.innerWidth < 768
  ) {
    return true;
  } else {
    return false;
  }
});

const isDark = ref(localStorage.getItem("theme") === "dark");

const toggleDark = () => {
  isDark.value = !isDark.value;
  localStorage.setItem("theme", isDark.value ? "dark" : "light");
  document.documentElement.classList.toggle("dark", isDark.value);
};

const formData = ref({
  name: "",
  email: "",
  subject: "",
  contact: "",
  content: "",
  url: "",
  files: [],
});
const pendingApprovals = ref([]);
// The dropdown shows the newest few; the badge has to reflect the whole queue,
// so the count comes from the API rather than from the list length.
const pendingApprovalsTotal = ref(0);
const APPROVAL_PREVIEW_SIZE = 10;
const pendingCustomMls = ref([]);

const isSuperAdmin = computed(() => isSuperAdminUser(userStore.getUser()));

const isAdmin = computed(() => userStore.getUser().role === "admin");

// The Worklist is open to admin roles and to super admins. Super admins are
// tested through variables.js rather than the local isSuperAdmin computed
// above, which carries an older two-email list and does not read the
// backend's is_super_admin flag.
const canSeeWorklist = computed(
  () => isAdmin.value || isSuperAdminUser(userStore.getUser())
);

const totalPendingCount = computed(() => {
  return (
    (pendingApprovalsTotal.value || 0) + (pendingCustomMls.value.length || 0)
  );
});

const userName = computed(() => {
  const u = userStore.getUser() || {};
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return name || u.mail || "Account";
});

const userInitials = computed(() => {
  const u = userStore.getUser() || {};
  const a = (u.first_name || "").trim().charAt(0);
  const b = (u.last_name || "").trim().charAt(0);
  const initials = (a + b).toUpperCase();
  if (initials) return initials;
  const m = (u.mail || "").trim();
  return m ? m.charAt(0).toUpperCase() : "U";
});

const userRoleLabel = computed(() => {
  const r = (userStore.getUser() || {}).role || "";
  if (!r) return "";
  return r.charAt(0).toUpperCase() + r.slice(1);
});

const avatarColor = computed(() => {
  const palette = [
    "#A03FB5",
    "#343376",
    "#1F9D55",
    "#E0A800",
    "#FC5868",
    "#14134F",
  ];
  const seed = (userName.value || "U").charCodeAt(0) || 0;
  return palette[seed % palette.length];
});

const fetchApprovals = () => {
  // This runs on every page load. It used to ask for the entire approval queue
  // -- every flagged row, each one a full node payload -- to render at most ten
  // dropdown entries and a number, and that request is what timed out. Ask for
  // one small page and take the badge count from the total the API reports.
  axios
    .get(`${BACKEND_URL}api/get_utc_dba_approval_list`, {
      params: { status: "pending", page: 0, page_size: APPROVAL_PREVIEW_SIZE },
    })
    .then((response) => {
      const data = response.data || {};
      pendingApprovals.value = Array.isArray(data.items) ? data.items : [];
      pendingApprovalsTotal.value = data.total || 0;
    })
    .catch((err) => {
      console.error("Error:", err);
      pendingApprovals.value = [];
      pendingApprovalsTotal.value = 0;
    });
};

const fetchMlsCustomList = () => {
  axios
    .get(`${BACKEND_URL}api/custom_mls_user/list`)
    .then((response) => {
      pendingCustomMls.value = Array.isArray(response.data)
        ? response.data
        : [];
    })
    .catch((err) => {
      console.error("Error:", err);
      pendingCustomMls.value = [];
    });
};
onMounted(() => {
  document.documentElement.classList.toggle("dark", isDark.value);
  fetchApprovals();
  fetchMlsCustomList();
});
</script>

<style scoped>
/* ───────────────────────── Layout ───────────────────────── */
.rpd-header {
  position: relative;
  background: #fff;
  z-index: 5 !important;
  color: #12283f;
  flex-shrink: 0;
  border-bottom: 1px solid #eef0f4;
  transition: background 0.3s;
}

.rpd-header__inner {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 14px 40px;
  min-height: 68px;
}

.rpd-header__spacer {
  flex: 1;
}

.rpd-header__divider {
  display: inline-block;
  width: 1px;
  height: 28px;
  background: #e9e8e9;
}

/* Logo */
.rpd-header__logo {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.rpd-header__logo-img {
  height: 36px;
  width: auto;
  display: block;
}

/* Workspace eyebrow */
.rpd-header__workspace {
  font-family: "Helvetica Light", Helvetica, Arial, sans-serif;
  font-size: 11px;
  color: #8a8a8a;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 500;
}

/* Actions cluster */
.rpd-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.rpd-header__actions>* {
  flex: 0 0 auto;
  margin: 0 !important;
}

.rpd-header__spacer {
  flex: 1 1 auto;
  min-width: 0;
}

/* Tighten bootstrap-vue dropdown wrapper inside the actions cluster */
:deep(.rpd-header__bdropdown.btn-group),
:deep(.rpd-header__bdropdown) {
  margin: 0 !important;
  padding: 0;
  display: inline-flex;
  vertical-align: middle;
  flex: 0 0 auto;
}

:deep(.rpd-header__bdropdown > .btn) {
  width: auto;
  min-width: 0;
  margin: 0 !important;
  flex: 0 0 auto !important;
}

/* ───────────────────────── Icon buttons ───────────────────────── */
.rpd-iconbtn,
.rpd-iconbtn--asdiv {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 1px solid #e2e4ea;
  background: #fff;
  color: #12283f;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  text-decoration: none;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1),
    border-color 120ms cubic-bezier(0.2, 0, 0, 1);
  padding: 0;
  font-size: 14px;
}

.rpd-iconbtn:hover,
.rpd-iconbtn:focus-visible {
  background: #fafbfd;
  border-color: #c9ccd2;
  color: #12283f;
  outline: none;
}

/* When the icon "button" is the b-dropdown's inner span */
.rpd-iconbtn--asdiv {
  cursor: pointer;
}

/* Hide bootstrap dropdown's link chrome around our icon */
:deep(.rpd-iconbtn-toggle) {
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  line-height: 1 !important;
  color: inherit !important;
}

:deep(.rpd-iconbtn-toggle:hover),
:deep(.rpd-iconbtn-toggle:focus) {
  text-decoration: none !important;
  background: transparent !important;
}

:deep(.rpd-iconbtn-toggle:hover .rpd-iconbtn--asdiv),
:deep(.rpd-iconbtn-toggle:focus .rpd-iconbtn--asdiv) {
  background: #fafbfd;
  border-color: #c9ccd2;
}

.rpd-iconbtn__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #fc5868;
  color: #fff;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 9px;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}

/* ─────────────────────── New project pill ─────────────────────── */
/* Same pill as the feedback button but outlined: two solid navy pills side by
   side would read as one control, and this is the less-used of the two. */
.rpd-newproject {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  background: transparent !important;
  color: #12283f !important;
  border: 1px solid #d7dce4 !important;
  border-radius: 999px !important;
  padding: 8px 16px 8px 14px !important;
  font-family: "Poppins", sans-serif !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-newproject:hover,
.rpd-newproject:focus {
  background: #f2f4f8 !important;
  color: #12283f !important;
}

.rpd-newproject :deep(svg) {
  width: 12px;
  height: 12px;
}

/* Kept in the scoped block, not the dark block below: both rules carry
   !important, so the scoped selector's extra attribute would otherwise win and
   leave the pill light on a dark header. */
html.dark .rpd-newproject {
  border-color: rgba(255, 255, 255, 0.16) !important;
  color: #d6e4f0 !important;
}

html.dark .rpd-newproject:hover,
html.dark .rpd-newproject:focus {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #fff !important;
}

/* ───────────────────────── Feedback pill ───────────────────────── */
.rpd-feedback {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  background: #12283f !important;
  color: #fff !important;
  border: none !important;
  border-radius: 999px !important;
  padding: 9px 16px 9px 18px !important;
  font-family: "Poppins", sans-serif !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-feedback:hover,
.rpd-feedback:focus {
  background: #1d3a5a !important;
  color: #fff !important;
}

.rpd-feedback :deep(svg) {
  width: 14px;
  height: 14px;
}

/* ───────────────────────── User chip ───────────────────────── */
:deep(.rpd-userchip-toggle) {
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  line-height: 1 !important;
  color: inherit !important;
}

:deep(.rpd-userchip-toggle:hover),
:deep(.rpd-userchip-toggle:focus) {
  text-decoration: none !important;
  background: transparent !important;
}

.rpd-userchip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 5px 12px 5px 5px;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  background: transparent;
  color: #12283f;
  font-family: inherit;
  transition: border-color 120ms cubic-bezier(0.2, 0, 0, 1);
}

:deep(.rpd-userchip-toggle:hover) .rpd-userchip,
:deep(.rpd-userchip-toggle:focus) .rpd-userchip {
  border-color: #c9ccd2;
}

.rpd-userchip__avatar {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #a03fb5;
  color: #fff;
  display: grid;
  place-items: center;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0;
}

.rpd-userchip__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  text-align: left;
}

.rpd-userchip__name {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 12.5px;
  color: #12283f;
}

.rpd-userchip__role {
  font-size: 10.5px;
  color: #8a8a8a;
  margin-top: 1px;
}

.rpd-userchip__chev {
  width: 14px;
  height: 14px;
  margin-left: 2px;
  opacity: 0.6;
}

/* Login link (anonymous) */
.rpd-header__login-link {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 12.5px;
  color: #12283f;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
}

.rpd-header__login-link:hover {
  color: #1d3a5a;
  text-decoration: none;
}

/* ───────────────────────── Dropdown menus ───────────────────────── */
:deep(.rpd-header__notif-menu),
:deep(.rpd-header__user-menu) {
  max-height: 1000px;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 260px;
  border: 1px solid #e2e4ea;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(18, 40, 63, 0.14),
    0 4px 8px rgba(18, 40, 63, 0.06);
  padding: 6px;
  margin-top: 8px;
}

/* Leading glyph on the user-menu rows, fixed width so the labels line up
   regardless of how wide each icon draws. */
:deep(.rpd-menu-icon) {
  width: 16px;
  margin-right: 10px;
  text-align: center;
  opacity: 0.7;
}

:deep(.rpd-header__notif-menu::-webkit-scrollbar) {
  width: 6px;
}

:deep(.rpd-header__notif-menu::-webkit-scrollbar-thumb) {
  background-color: #d0d4dc;
  border-radius: 10px;
}

/* ───────────────────────── Front (landing) variant ───────────────────────── */
.fw-header {
  position: absolute;
  top: 0;
  z-index: 5;
  width: 100%;
}

.main-header.front {
  position: sticky;
  top: 0;
  width: 100%;
  background: transparent;
  border-bottom: 0;
}

.main-header.bg-vb-primary {
  background: #12283f;
  border-bottom: 0;
}

/* ───────────────────────── Responsive ───────────────────────── */
@media (max-width: 991px) {
  .rpd-header__inner {
    padding: 10px 16px;
    gap: 12px;
  }

  .rpd-header__logo-img {
    height: 30px;
  }

  .rpd-header__workspace {
    display: none;
  }

  .rpd-feedback span,
  .rpd-newproject span {
    display: none;
  }

  .rpd-feedback {
    padding: 9px 12px !important;
  }

  .rpd-newproject {
    padding: 8px 12px !important;
  }

  .mobile-nav {
    font-size: 2rem;
    cursor: pointer;
  }
}

@media (max-width: 575px) {
  .rpd-header__inner {
    padding: 8px 12px;
    gap: 8px;
  }

  .rpd-header__actions {
    gap: 6px;
  }
}
</style>

<style>
/* ── Dark mode ── */
html.dark,
html.dark body {
  background-color: #0e1b2c !important;
  color: #d6e4f0 !important;
}

/* Surfaces */
html.dark .card,
html.dark .b-card,
html.dark .modal-content,
html.dark .dropdown-menu,
html.dark .popover,
html.dark .tooltip-inner,
html.dark .b-sidebar,
html.dark .sidebar,
html.dark [class*="panel"],
html.dark [class*="box-widget"],
html.dark [class*="list-single"] {
  background-color: #152030 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  color: #d6e4f0 !important;
}

/* Header */
html.dark .main-header {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

html.dark .bg-white,
html.dark .bg-light {
  background-color: #152030 !important;
}

/* Tables */
html.dark table,
html.dark .table,
html.dark th,
html.dark td,
html.dark tr {
  background-color: #0e1b2c !important;
  color: #d6e4f0 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

html.dark .table-striped tbody tr:nth-of-type(odd) {
  background-color: #152030 !important;
}

html.dark .table-hover tbody tr:hover {
  background-color: #1d3048 !important;
}

/* Inputs */
html.dark input,
html.dark select,
html.dark textarea,
html.dark .form-control,
html.dark .custom-select {
  background-color: #152030 !important;
  color: #d6e4f0 !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

html.dark input::placeholder,
html.dark textarea::placeholder {
  color: rgba(214, 228, 240, 0.45) !important;
}

/* Dropdowns */
html.dark .dropdown-item {
  color: #d6e4f0 !important;
}

html.dark .dropdown-item:hover,
html.dark .dropdown-item:focus {
  background-color: #1d3048 !important;
}

html.dark .dropdown-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .dropdown-header {
  color: rgba(214, 228, 240, 0.55) !important;
}

/* Text overrides */
html.dark .text-dark,
html.dark .text-muted {
  color: rgba(214, 228, 240, 0.6) !important;
}

/* Borders */
html.dark .border,
html.dark .border-bottom,
html.dark .border-top {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Header icon button - dark */
html.dark .rpd-iconbtn,
html.dark .rpd-iconbtn--asdiv {
  background: #152030 !important;
  border-color: rgba(214, 228, 240, 0.18) !important;
  color: #d6e4f0 !important;
}

html.dark .rpd-iconbtn:hover,
html.dark .rpd-iconbtn:focus-visible {
  background: #1d3048 !important;
  border-color: rgba(214, 228, 240, 0.32) !important;
}

html.dark .rpd-iconbtn__badge {
  box-shadow: 0 0 0 2px #0e1b2c !important;
}

/* Feedback pill - dark */
html.dark .rpd-feedback {
  background: #d6e4f0 !important;
  color: #0e1b2c !important;
}

html.dark .rpd-feedback:hover,
html.dark .rpd-feedback:focus {
  background: #fff !important;
  color: #0e1b2c !important;
}

/* User chip - dark */
html.dark .rpd-userchip {
  border-color: rgba(214, 228, 240, 0.18);
  color: #d6e4f0;
}

html.dark .rpd-userchip__name {
  color: #d6e4f0;
}

html.dark .rpd-userchip__role {
  color: rgba(214, 228, 240, 0.55);
}

/* Divider - dark */
html.dark .rpd-header__divider {
  background: rgba(214, 228, 240, 0.18);
}

/* Workspace eyebrow - dark */
html.dark .rpd-header__workspace {
  color: rgba(214, 228, 240, 0.6);
}

/* Login link - dark */
html.dark .rpd-header__login-link {
  color: #d6e4f0;
}

/* Bell icon */
html.dark .text-primary {
  color: #4db7fe !important;
}

/* vb-primary text/links */
html.dark .text-vb-primary,
html.dark a.text-vb-primary,
html.dark a.text-vb-primary:hover,
html.dark a.text-vb-primary:focus {
  color: #d6e4f0 !important;
}

/* outline-vb-primary buttons */
html.dark .btn-outline-vb-primary,
html.dark .btn-outline-vb-primary:disabled {
  color: #d6e4f0 !important;
  border-color: rgba(214, 228, 240, 0.5) !important;
  background: transparent !important;
}

html.dark .btn-outline-vb-primary:hover,
html.dark .btn-outline-vb-primary:focus,
html.dark .btn-outline-vb-primary:active {
  color: #0e1b2c !important;
  background: #d6e4f0 !important;
  border-color: #d6e4f0 !important;
}

div#feedback-modal___BV_modal_body_ {
  padding: 0px !important;
}
</style>
